import i18n from "@/src/i18n";
import type { MaybeValidProps } from "@core/domain/models/maybe-valid";
import type { Nullable } from "@core/domain/types/nullable.type";
import type { Unbrand } from "@core/domain/types/unbrand.type";
import type { Id } from "@core/domain/value-objects/id.value-object";
import { zIdSchema } from "@core/domain/value-objects/id.value-object";
import type { ModelError, PartialValid } from "@iot-platform/core";
import {
    NonRecuperableModelError,
    RecuperableModelError,
    partialSafeParse,
    zDateTimeSchema,
} from "@iot-platform/core";
import { merge } from "es-toolkit";
import { DateTime } from "luxon";
import type { Result } from "neverthrow";
import { err, ok } from "neverthrow";
import * as z from "zod";

const MAX_FILE_SIZE_MB = 5242880;

const NotebookMessageFileSchema = z
    .object({
        id: zIdSchema,
        url: z.string().trim(),
    })
    .brand<"NotebookMessageFile">();

const NotebookMessageSchema = z
    .object({
        id: zIdSchema,
        title: z.string().trim(),
        description: z.string().trim(),
        time: z.string().trim(),
        userId: z.number().min(1),
        userEmail: z.email(),
        files: z.array(NotebookMessageFileSchema).nullable(),
    })
    .brand<"NotebookMessage">();

export type NotebookMessageInputProps = z.input<typeof NotebookMessageSchema>;
export type NotebookMessageProps = z.output<typeof NotebookMessageSchema>;

export class NotebookMessage {
    private constructor(
        private readonly props: Readonly<NotebookMessageProps>,
    ) {}

    get id(): Id {
        return this.props.id;
    }

    get title(): string {
        return this.props.title;
    }

    get description(): string {
        return this.props.description;
    }

    get time(): string {
        return this.props.time;
    }

    get userId(): number {
        return this.props.userId;
    }

    get userEmail(): string {
        return this.props.userEmail;
    }

    get files(): Nullable<z.output<typeof NotebookMessageFileSchema>[]> {
        return this.props.files;
    }

    static validate(
        props: Readonly<MaybeValidProps<NotebookMessageInputProps>>,
    ): Result<NotebookMessageProps, z.ZodError<NotebookMessageProps>> {
        const result = NotebookMessageSchema.safeParse(props);

        return result.success ? ok(result.data) : err(result.error);
    }

    private static partialValidate(
        props: Readonly<MaybeValidProps<NotebookMessageInputProps>>,
    ): Result<NotebookMessageProps, PartialValid<typeof NotebookMessage>> {
        const result = partialSafeParse(NotebookMessageSchema, props);

        return result.success
            ? ok(result.data)
            : err({
                  validProps: result.validData,
                  errors: result.error,
              });
    }

    static create(
        props: Readonly<NotebookMessageInputProps>,
    ): Result<NotebookMessage, z.ZodError<NotebookMessageProps>> {
        return NotebookMessage.validate(props).map(
            (validatedProps) => new NotebookMessage(validatedProps),
        );
    }

    static createWithRecovery(
        props: NotebookMessageInputProps,
    ): Result<NotebookMessage, ModelError<typeof NotebookMessage>> {
        return NotebookMessage.partialValidate(props)
            .mapErr((error) => {
                if (!error.validProps.id) return new NonRecuperableModelError();

                const defaultProps: Unbrand<NotebookMessageProps> = {
                    id: error.validProps.id,
                    title: "",
                    description: "",
                    time: "",
                    userId: 0,
                    userEmail: "",
                    files: null,
                };

                const recoveryProps: Unbrand<NotebookMessageProps> = merge(
                    defaultProps,
                    error.validProps,
                );

                return new RecuperableModelError<typeof NotebookMessage>(
                    new NotebookMessage(recoveryProps as NotebookMessageProps),
                    error.errors,
                );
            })
            .map((validatedProps) => new NotebookMessage(validatedProps));
    }

    toCreateProps(): NotebookMessageInputProps {
        return {
            id: this.props.id.toCreateProps(),
            title: this.props.title,
            description: this.props.description,
            time: this.props.time,
            userId: this.props.userId,
            userEmail: this.props.userEmail,
            files:
                this.props.files?.map((file) => ({
                    ...file,
                    id: file.id.toCreateProps(),
                })) ?? null,
        };
    }
}

const NotebookMessagesSchema = z
    .object({
        notebookMessages: z.array(
            z.custom<NotebookMessage>((val) => val instanceof NotebookMessage, {
                error: "Invalid NotebookMessage instance",
            }),
        ),
        count: z.number().min(0),
    })
    .brand<"NotebookMessages">();

export type NotebookMessagesInputProps = z.input<typeof NotebookMessagesSchema>;
export type NotebookMessagesProps = z.output<typeof NotebookMessagesSchema>;

export class NotebookMessages {
    private constructor(
        private readonly props: Readonly<NotebookMessagesProps>,
    ) {}

    get notebookMessages(): readonly Readonly<NotebookMessage>[] {
        return this.props.notebookMessages;
    }

    get count(): number {
        return this.props.count;
    }

    static empty(): NotebookMessages {
        return NotebookMessages.create({
            notebookMessages: [],
            count: 0,
        })._unsafeUnwrap();
    }

    static validate(
        props: Readonly<MaybeValidProps<NotebookMessagesInputProps>>,
    ): Result<NotebookMessagesProps, z.ZodError<NotebookMessagesProps>> {
        const result = NotebookMessagesSchema.safeParse(props);

        return result.success ? ok(result.data) : err(result.error);
    }

    static create(
        props: Readonly<NotebookMessagesInputProps>,
    ): Result<NotebookMessages, z.ZodError<NotebookMessagesProps>> {
        return NotebookMessages.validate(props).map(
            (validatedProps) => new NotebookMessages(validatedProps),
        );
    }
}

export const NotebookMessageCreateSchema = z
    .object({
        title: z.string().trim().min(1),
        description: z.string().trim().min(1),
        time: zDateTimeSchema.optional(),
        userId: z.number().min(1),
    })
    .superRefine((notebookMessage, ctx) => {
        if (
            notebookMessage.time &&
            notebookMessage.time >=
                DateTime.now().setLocale(i18n.language).plus({ hours: 2 })
        ) {
            ctx.addIssue({
                code: "custom",
                message: i18n.t(
                    "validation:models.notebookMessage.dayGreaterThanTodayPlus2Hours",
                ),
                path: ["time"],
            });
        }
    });
export type NotebookMessageCreateInputProps = z.input<
    typeof NotebookMessageCreateSchema
>;
export type NotebookMessageCreate = z.output<
    typeof NotebookMessageCreateSchema
>;

export const NotebookMessageUpdateSchema = z
    .object({
        id: z.number().positive(),
        title: z.string().trim().min(1),
        description: z.string().trim().min(1),
        time: zDateTimeSchema.optional(),
        userId: z.number().min(1),
    })
    .superRefine((notebookMessage, ctx) => {
        if (
            notebookMessage.time &&
            notebookMessage.time >=
                DateTime.now().setLocale(i18n.language).plus({ hours: 2 })
        ) {
            ctx.addIssue({
                code: "custom",
                message: i18n.t(
                    "validation:models.notebookMessage.dayGreaterThanTodayPlus2Hours",
                ),
                path: ["time"],
            });
        }
    });

export type NotebookMessageUpdateInputProps = z.input<
    typeof NotebookMessageUpdateSchema
>;
export type NotebookMessageUpdate = z.output<
    typeof NotebookMessageUpdateSchema
>;

export const NotebookMessageFileCreateSchema = z.object({
    files: z
        .array(
            z
                .custom<File>((value) => value instanceof File, {
                    error: () =>
                        i18n.t(
                            "validation:models.notebookMessage.fileRequired",
                        ),
                })
                .refine((file) => file.size <= MAX_FILE_SIZE_MB, {
                    error: () =>
                        i18n.t(
                            "validation:models.notebookMessage.fileTooLarge",
                        ),
                }),
        )
        .optional(),
});

export type NotebookMessageFileCreate = z.output<
    typeof NotebookMessageFileCreateSchema
>;
